import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export interface UseCaseChip {
  icon: LucideIcon;
  label: ReactNode;
  to: string;
  color?: string;
}

interface ProductCardProps {
  icon: LucideIcon;
  name: ReactNode;
  description: ReactNode;
  /** internal router link */
  to?: string;
  /** external URL (opens new tab) */
  href?: string;
  /** accent theme for icon background + hover border */
  accent?: 'emerald' | 'blue' | 'indigo' | 'amber' | 'cyan' | 'red';
  /** chips shown beneath the description (e.g. use-case shortcuts) */
  chips?: UseCaseChip[];
  ctaLabel?: ReactNode;
  featured?: boolean;
  className?: string;
}

const ACCENT_MAP: Record<NonNullable<ProductCardProps['accent']>, { icon: string; bg: string; ring: string; chip: string }> = {
  emerald: { icon: 'text-emerald-500', bg: 'bg-emerald-500/10', ring: 'hover:border-emerald-500/50', chip: 'text-emerald-500' },
  blue:    { icon: 'text-blue-500',    bg: 'bg-blue-500/10',    ring: 'hover:border-blue-500/50',    chip: 'text-blue-500' },
  indigo:  { icon: 'text-indigo-500',  bg: 'bg-indigo-500/10',  ring: 'hover:border-indigo-500/50',  chip: 'text-indigo-500' },
  amber:   { icon: 'text-amber-500',   bg: 'bg-amber-500/10',   ring: 'hover:border-amber-500/50',   chip: 'text-amber-500' },
  cyan:    { icon: 'text-cyan-500',    bg: 'bg-cyan-500/10',    ring: 'hover:border-cyan-500/50',    chip: 'text-cyan-500' },
  red:     { icon: 'text-red-500',     bg: 'bg-red-500/10',     ring: 'hover:border-red-500/50',     chip: 'text-red-500' },
};

export default function ProductCard({
  icon: Icon,
  name,
  description,
  to,
  href,
  accent = 'emerald',
  chips,
  ctaLabel,
  featured = false,
  className = '',
}: ProductCardProps) {
  const a = ACCENT_MAP[accent];
  const isExternal = !!href;
  const hasChips = !!chips && chips.length > 0;

  const inner = (
    <>
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className={`h-12 w-12 rounded-xl ${a.bg} flex items-center justify-center flex-shrink-0`}>
          <Icon className={`h-6 w-6 ${a.icon}`} />
        </div>
        {isExternal ? (
          <ExternalLink className="h-4 w-4 text-muted-foreground/60 group-hover:text-foreground transition-colors" />
        ) : (
          <ArrowRight className="h-4 w-4 text-muted-foreground/60 group-hover:translate-x-1 group-hover:text-foreground transition-all" />
        )}
      </div>
      <h3 className="text-lg font-semibold text-foreground mb-2">{name}</h3>
      <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>

      {hasChips && (
        <div className="mt-5 flex flex-wrap gap-2 relative z-10">
          {chips!.map((chip, idx) => {
            const ChipIcon = chip.icon;
            return (
              <Link
                key={idx}
                to={chip.to}
                onClick={(e) => e.stopPropagation()}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-border bg-background/60 text-xs ${chip.color || a.chip} hover:bg-accent hover:border-foreground/30 transition-colors`}
              >
                <ChipIcon className="h-3 w-3" />
                {chip.label}
              </Link>
            );
          })}
        </div>
      )}

      {ctaLabel && (
        <div className={`mt-5 inline-flex items-center gap-1.5 text-sm font-medium ${a.icon} relative z-10`}>
          {ctaLabel}
          <ArrowRight className="h-3.5 w-3.5" />
        </div>
      )}
    </>
  );

  const cls = `group relative flex flex-col p-6 rounded-2xl border border-border bg-card transition-all ${a.ring} hover:shadow-lg hover:-translate-y-0.5 ${featured ? 'md:col-span-2' : ''} ${className}`;
  const stretchedLinkCls = 'absolute inset-0 rounded-2xl z-0';

  if (isExternal) {
    if (hasChips) {
      return (
        <div className={cls}>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={typeof name === 'string' ? name : undefined}
            className={stretchedLinkCls}
          />
          {inner}
        </div>
      );
    }
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={cls}>
        {inner}
      </a>
    );
  }
  if (to) {
    if (hasChips) {
      return (
        <div className={cls}>
          <Link
            to={to}
            aria-label={typeof name === 'string' ? name : undefined}
            className={stretchedLinkCls}
          />
          {inner}
        </div>
      );
    }
    return <Link to={to} className={cls}>{inner}</Link>;
  }
  return <div className={cls}>{inner}</div>;
}
